import React from "react";
import EditorLayout from "layouts/EditorLayout";
import EditorHeader from "./components/EditorHeader";
import ProductHeader from "./components/ProductHeader";
import { observer } from "mobx-react-lite";
import GeneralInformation from "./features/GeneralInformation";
import CustomProperties from "../PropertyManager/features/CustomProperties";
import Circularity from "./features/Circularity";
import EnvironmentalData from "./features/EnvironmentalData";
import clsx from "clsx";
import useToast from "hooks/useToast";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  CPXValueOUT,
  ComponentDetailsByFileOut,
  EPDGroupOUT,
  EPDSOUT,
  FileCategoryType,
  PrefilledProductOUT,
  ProductProperty,
  InternalProductSearchOutput,
  ResultOUT,
  S3PostData,
  SingleItem,
} from "api-client";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import { searchStore } from "features/ProductSearch/SearchStore";
import { useMappingTools } from "features/MappingEditor/useMappingTools";
import { AxiosError } from "axios";
import { getCurrentReferenceUnitValue } from "features/MappingEditor/utils";
import LoadingIcon from "components/LoadingIcon";
import {
  CustomProduct,
  BPEStore,
  Option,
  EPDRowProps,
  Property,
} from "./ProductEditorStore";
import ProductImages from "./features/ProductImages";
import { useUploadToS3 } from "hooks/useUploadToS3";
import { epdSearchStore } from "features/EPDSearchModal/EPDSearchStore";
import ProductFiles from "./features/ProductFiles";
import GraphicObject from "./features/ProductGraphic";
import { propertyManagerStore } from "features/PropertyManager/PropertyManagerStore";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";
import ProductUsageWarning from "features/ProductUsage/ProductUsageWarning";
import useMappingEditor from "features/MappingEditor/useMappingEditor";
import { ProductActionMode } from "utils";

const BuildingProductEditor = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const location = useLocation();
  const { mappingToObjects } = useMappingEditor();
  const { onCloseEditor } = useMappingTools();
  const [loading, setLoading] = React.useState(true);
  const { uploadFiles: uploadToS3 } = useUploadToS3();
  const productID = location.state?.id;

  const uploadImages = async () => {
    const imagesToUpload = BPEStore.imagesToUpload;
    if (!imagesToUpload?.length) return;

    const imagesMeta = imagesToUpload.map((item) => ({
      file_name: item.name,
      content_type: item?.type,
      category: FileCategoryType.Image,
    }));

    const presignedPosts: void | S3PostData[] = await BPEStore.uploadFilesInfo(
      imagesMeta
    );
    if (!presignedPosts) return;
    const result = await uploadToS3(imagesToUpload, presignedPosts);
    if (!result) return;
    const uploadedImagesIDs = result.map((item) => item.file_id);
    BPEStore.setUploadedImagesIDs(uploadedImagesIDs);
  };

  const uploadFiles = async () => {
    const filesToUpload = BPEStore.filesToUpload;
    if (!filesToUpload?.length) return;

    const filesMeta = filesToUpload.map((item) => ({
      file_name: item.name,
      content_type: item?.type,
      category: FileCategoryType.Document,
    }));

    const presignedPosts: void | S3PostData[] = await BPEStore.uploadFilesInfo(
      filesMeta
    );
    if (!presignedPosts) return;
    const result = await uploadToS3(filesToUpload, presignedPosts);
    if (!result) return;
    const uploadedFilesIDs = result.map((item) => item.file_id);
    BPEStore.setUploadedFilesIDs(uploadedFilesIDs);
  };

  const uploadDiagrams = async () => {
    const filesToUpload = BPEStore.diagramsToUpload;
    if (!filesToUpload?.length) return;

    const filesMeta = filesToUpload.map((item) => ({
      file_name: item.name,
      content_type: item?.type,
      category: FileCategoryType.Diagram,
    }));

    const presignedPosts: void | S3PostData[] = await BPEStore.uploadFilesInfo(
      filesMeta
    );
    if (!presignedPosts) return;
    const result = await uploadToS3(filesToUpload, presignedPosts);
    if (!result) return;
    const uploadedFilesIDs = result.map((item) => item.file_id);
    BPEStore.setUploadedDiagramIDs(uploadedFilesIDs);
  };

  async function createProduct(
    doAdd: boolean | undefined,
    doMatch: boolean | undefined
  ) {
    const createdItem = await BPEStore.registerCustomProduct();
    if (doAdd && createdItem) {
      COEStore.setOnTheFlyProductAdded(
        createdItem as ComponentDetailsByFileOut
      );
    }
    if (doMatch && createdItem?.id) {
      await mappingToObjects(
        "manual_match",
        createdItem.id,
        MEStore.currentRow?.original as SingleItem
      );
    }
    toast(t("productEditor.successMsgOnSave"), "success");
    return createdItem?.id;
  }

  async function updateProduct(
    doAdd: boolean | undefined,
    doMatch: boolean | undefined
  ) {
    const response = await BPEStore.updateCustomProduct();
    if (doAdd && response) {
      COEStore.setOnTheFlyProductAdded(
        response.data as unknown as ComponentDetailsByFileOut
      );
    }
    if (doMatch) {
      const productID = response.data.id;
      await mappingToObjects(
        "manual_match",
        productID,
        MEStore.currentRow?.original as SingleItem
      );
    }
    if (response.data.is_recalculation_triggered) {
      toast(t("productEditor.successMsgOnEditWithRecalculation"), "success");
    } else {
      toast(t("productEditor.successMsgOnEdit"), "success");
    }
  }

  const saveAndClose = async (doAdd?: boolean, doMatch?: boolean) => {
    try {
      await uploadImages();
      await uploadFiles();
      await uploadDiagrams();
      if (location.state?.mode === ProductActionMode.Edit) {
        await updateProduct(doAdd, doMatch);
      } else {
        await createProduct(doAdd, doMatch);
      }
      searchStore.setComponentDetails(undefined);
      BPEStore.resetData();
      const isEPDSearchOpen = searchStore.openEPDSearch.state;
      if (isEPDSearchOpen) {
        backToProductSearchFromEPDSearch();
      }
      onCloseEditor(undefined, doAdd || doMatch, location.state?.isDetails);
    } catch (error) {
      toast(
        `${t("productEditor.warningMsgOnSave")} ${
          (
            ((error as AxiosError)?.response?.data as ResultOUT)
              ?.message as ResultOUT
          )?.message ?? error
        }`,
        "warning"
      );
    }

    function backToProductSearchFromEPDSearch() {
      searchStore.setOpenProductSearch({
        state: !doMatch,
        term: searchStore.openEPDSearch.term,
      });
      searchStore.setOpenEPDSearch({ state: false });
    }
  };

  const columnStyle = clsx(
    "px-6 pt-6 text-gray-800 xl:max-h-[calc(100vh-138px)] max-h-auto lg:overflow-y-auto"
  );

  async function getPropertyConfigs() {
    await propertyManagerStore.getAllPropertyConfigs();
    BPEStore.onChangeProperties(propertyManagerStore.allPropertyConfigs);
  }

  async function runProductInitialization(
    loadProductData?: () => Promise<void>
  ) {
    setLoading(true);

    await getPropertyConfigs();
    await loadProductData?.();
    await BPEStore.getProductEPDSpecifications();
    await BPEStore.getAllGroupsInsights();
    BPEStore.checkSaveValidity();

    setLoading(false);
  }

  const loadEditOrDuplicate = async () => {
    await loadDataForUpdate();
  };

  const loadDataFromEPD = async () => {
    const currentRowID = (MEStore.currentRow?.original as SingleItem)?.id;
    const selectedRowsObjectIDs = MEStore.selectedRows.map(
      (item) => item.id as string
    );
    const objectIDs = selectedRowsObjectIDs.length
      ? selectedRowsObjectIDs
      : currentRowID
      ? [currentRowID]
      : [];
    const data = await BPEStore.getPrefilledProductData(
      location.state.id,
      location.state.isExternal,
      objectIDs
    );
    if (data) {
      prefillProduct(data);
    }
  };

  const initialLoad = async () => {
    await getListItems();

    switch (location.state?.mode) {
      case ProductActionMode.Edit:
      case ProductActionMode.Duplicate:
        await runProductInitialization(loadEditOrDuplicate);
        break;
      case ProductActionMode.Generate:
        await runProductInitialization(loadDataFromEPD);
        break;
      default:
        // create new product
        BPEStore.resetData();
        await getPropertyConfigs();
        setLoading(false);
    }
  };

  React.useEffect(() => {
    (async () => await initialLoad())();

    epdSearchStore.setIsOrganisationalSearch(false);

    return () => BPEStore.resetData();
  }, []);

  async function getListItems() {
    await BPEStore.fetchCPXValues();
    await BPEStore.getDinCategoriesLevel1();
  }

  const normalizeGroup = (group: EPDGroupOUT) => {
    return (
      group.epds?.map((item: EPDSOUT) => {
        return {
          ...item,
          factor: item.factor,
          epd: { ...item.epd, database: item.epd.database },
        } as EPDRowProps;
      }) ?? []
    );
  };

  const getDinCategoryID = (
    din_category_number: string | undefined | null,
    din_category: string | undefined | null
  ) => {
    return BPEStore.optionsLists.din_category?.find(
      (item) => item.name === `${din_category_number} ${din_category}`
    )?.id as string;
  };

  const mapGroups = (groups: EPDGroupOUT[] = []) => {
    return groups.map((group) => ({
      conformity: group.conformity,
      certifications: group.certifications,
      epds: normalizeGroup(group),
      health_checked: group.health_checked,
      is_health_checked: group.is_health_checked,
      generate_graphic: group.generate_graphic,
    }));
  };

  const findQualificationId = (
    list: (CPXValueOUT | Option)[],
    value?: string,
    includes = false
  ) => {
    return includes
      ? list.find((item) => item.name?.includes(value as string))?.id
      : list.find((item) => item.name === value)?.id;
  };

  const mapQualifications = (data: any) => ({
    separability_score: undefined,
    deconstructability_score: undefined,
    reusability_score: undefined,
    recyclability_score: undefined,

    recyclability_qualification: data.recyclability_qualification,
    separability_qualification: findQualificationId(
      BPEStore.optionsLists.separability_qualification,
      data.separability_qualification
    ),
    deconstructability_qualification: findQualificationId(
      BPEStore.optionsLists.deconstructability_qualification,
      data.deconstructability_qualification,
      true
    ),
    reusability_qualification: findQualificationId(
      BPEStore.optionsLists.reusability_qualification,
      data.reusability_qualification
    ),
    preuse_qualification: findQualificationId(
      BPEStore.optionsLists.preuse_qualification,
      data.preuse_qualification
    ),
  });

  function mapProductToStoreData(data: InternalProductSearchOutput) {
    return {
      id: String(data?.id ?? ""),
      name: String(data?.name ?? ""),
      dataset_source: data.dataset_source,
      din_category: getDinCategoryID(
        data.din_category_number,
        data.din_category
      ),
      life_span: data.life_span,
      auto_mapping_tags: data.tags_automapper ?? [],
      reference_unit: getCurrentReferenceUnitValue(
        data?.reference_unit?.toUpperCase()
      ),
      is_part_of_a_component: data.is_part_of_a_component,
      groups: mapGroups(data?.groups ?? []),
      ...mapQualifications(data),
    } as CustomProduct;
  }

  const mapProperties = (
    properties: Property[],
    orgProperties?: { [key: string]: ProductProperty }
  ) =>
    properties.map(
      (item) =>
        ({
          id: item.slug,
          slug: item.slug,
          name: item.name,
          property_type: item.property_type,
          value: orgProperties?.[item.slug]?.value,
          options: item.options,
        } as Property)
    );

  async function loadDataForUpdate() {
    const currentItem: InternalProductSearchOutput | undefined =
      await BPEStore.retrieveFullProductDetails(productID);

    if (!currentItem) return;

    BPEStore.setData({
      ...mapProductToStoreData(currentItem),

      images:
        currentItem.files_info?.images.map((item) => ({
          id: item.file_id,
          image: item.url,
        })) ?? [],

      files:
        currentItem.files_info?.documents.map((item) => ({
          file_id: item.file_id,
          file_name: item.file_name,
          url: item.url,
          created: item.created,
        })) ?? [],

      properties: mapProperties(
        BPEStore.data.properties,
        currentItem.org_properties
      ),
    } as CustomProduct);
  }

  function prefillProduct(data: PrefilledProductOUT) {
    BPEStore.setData({
      ...mapProductToStoreData(data),
      images: [],
      files: [],
      diagrams: [],
      properties: BPEStore.data.properties,
    });

    BPEStore.setShowTooltip({
      name: data.tooltips?.name,
      preuse_qualification: data.tooltips?.cpx_preuse,
      din_category: data.tooltips?.din_category,
      life_span: data.tooltips?.life_span,
      deconstructability_qualification: data.tooltips?.cpx_deconstructability,
      reusability_qualification: data.tooltips?.cpx_reusability,
      separability_qualification: data.tooltips?.cpx_separability,
    });
  }

  return (
    <EditorLayout
      header={
        <div>
          <EditorHeader
            saveAndClose={saveAndClose}
            disabled={BPEStore.saveValidationErrors}
          />
          <ProductHeader name={BPEStore.data.name} />
        </div>
      }
      className={clsx(
        "xl:!grid-cols-2 !grid-cols-1 !max-h-[calc(100vh-138px)] !min-h-[calc(100vh-138px)]",
        "lg:max-h-full bg-white overflow-y-auto",
        { "!flex w-full items-center justify-center": loading }
      )}
    >
      {loading ? (
        <LoadingIcon type="secondary" className="m-auto !w-20 !h-20" />
      ) : (
        <>
          <div className={clsx(columnStyle, "xl:border-r xl:pb-6")}>
            {BPEStore.data.is_part_of_a_component &&
              location.state?.mode === ProductActionMode.Edit && (
                <ProductUsageWarning id={productID} />
              )}

            <GeneralInformation />
            <ProductImages />
            <Circularity />
            <CustomProperties />
            <ProductFiles />
            <GraphicObject />
          </div>
          <div className={columnStyle}>
            <EnvironmentalData />
          </div>
        </>
      )}
    </EditorLayout>
  );
};

export default observer(BuildingProductEditor);
